import Route from '@ember/routing/route';
import type Transition from '@ember/routing/transition';
import { service } from '@ember/service';
import type Model from '@ember-data/model';
import type Store from 'ember-data/store';
import type GlobalFlow from 'garaje/models/global-flow';
import type Questionnaire from 'garaje/models/questionnaire';
import type { GlobalOverviewQuestionnairesRouteModel } from 'garaje/pods/location-overview/questionnaires/route';
import type StateService from 'garaje/services/state';
import type TransitionConfirmService from 'garaje/services/transition-confirm';
import { routeEvent } from 'garaje/utils/decorators/route';
import { TrackedSet } from 'tracked-built-ins';

interface GlobalOverviewQuestionnairesShowRouteParams {
  id: string;
}

export interface GlobalOverviewQuestionnairesShowRouteModel {
  changes: Set<Model>;
  fetchFlowsPage: GlobalOverviewQuestionnairesRouteModel['fetchFlowsPage'];
  flows: GlobalFlow[];
  questionnaire: Questionnaire;
}

export default class GlobalOverviewQuestionnairesShowRoute extends Route {
  @service declare state: StateService;
  @service declare store: Store;
  @service declare transitionConfirm: TransitionConfirmService;

  async model({
    id,
  }: GlobalOverviewQuestionnairesShowRouteParams): Promise<GlobalOverviewQuestionnairesShowRouteModel> {
    const { fetchFlowsPage, flows } = <GlobalOverviewQuestionnairesRouteModel>(
      this.modelFor('location-overview.questionnaires')
    );

    const existingRecord = this.store.peekRecord('questionnaire', id);
    if (existingRecord) {
      existingRecord.unloadRecord();
    }
    const questionnaire = await this.store.findRecord('questionnaire', id, {
      include: [
        'company',
        'flow-questionnaires',
        'flow-questionnaires.flow',
        'flow-questionnaires.flow.location',
        'questionnaire-sections',
        'questionnaire-sections.questionnaire-fields',
        'questionnaire-sections.questionnaire-fields.questionnaire-field-options',
      ].join(','),
      reload: true,
    });

    return {
      changes: new TrackedSet<Model>(),
      fetchFlowsPage,
      flows,
      questionnaire,
    };
  }

  @routeEvent
  routeWillChange(transition: Transition): void {
    const { changes } = <GlobalOverviewQuestionnairesShowRouteModel>this.modelFor(this.routeName);

    // no need to display confirmation modal when nothing is changed
    if (changes.size === 0) return;

    // display confirmation modal if questionnaire is dirty
    void this.transitionConfirm.displayConfirmTask.perform(transition, {
      header: 'There are unsaved changes to this assessment.',
      body: 'You will not be able to recover the changes if you proceed.',
      confirmText: 'Proceed anyway',
      continue() {
        for (const changedModel of changes) {
          changedModel.rollbackAttributes();
        }
        changes.clear();
      },
    });
  }
}
